import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  ArticleStatus,
  CreateKbArticleInput,
  CreateKbCategoryInput,
  CreateKbWebSourceInput,
  DataEnvelope,
  KbArticle,
  KbCategory,
  KbWebSource,
  ListEnvelope,
  UpdateKbArticleInput,
  UpdateKbCategoryInput,
} from './types';

export interface ArticleFilters {
  q?: string;
  categoryId?: string;
  status?: ArticleStatus | '';
}

export const kbKey = ['kb'] as const;

export function useKbCategories() {
  return useQuery({
    queryKey: [...kbKey, 'categories'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<KbCategory[]>>('/kb/categories', { signal }).then((r) => r.data),
  });
}

export function useKbArticlesInfinite(filters: ArticleFilters) {
  return useInfiniteQuery({
    queryKey: [...kbKey, 'articles', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<ListEnvelope<KbArticle>>('/kb/articles', {
        query: { ...filters, limit: 25, cursor: pageParam },
        signal,
      }),
    getNextPageParam: (last) => last.pagination.nextCursor ?? undefined,
  });
}

export function useKbArticle(id: string | undefined) {
  return useQuery({
    queryKey: [...kbKey, 'article', id],
    enabled: !!id,
    queryFn: ({ signal }) =>
      api<DataEnvelope<KbArticle>>(`/kb/articles/${id}`, { signal }).then((r) => r.data),
  });
}

function useInvalidateKb() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: kbKey });
}

export function useSaveKbCategory() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateKbCategoryInput | UpdateKbCategoryInput }) =>
      (id
        ? api<DataEnvelope<KbCategory>>(`/kb/categories/${id}`, { method: 'PATCH', body: input })
        : api<DataEnvelope<KbCategory>>('/kb/categories', { method: 'POST', body: input })
      ).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeleteKbCategory() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/kb/categories/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useSaveKbArticle() {
  const qc = useQueryClient();
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateKbArticleInput | UpdateKbArticleInput }) =>
      (id
        ? api<DataEnvelope<KbArticle>>(`/kb/articles/${id}`, { method: 'PATCH', body: input })
        : api<DataEnvelope<KbArticle>>('/kb/articles', { method: 'POST', body: input })
      ).then((r) => r.data),
    onSuccess: (a) => {
      qc.setQueryData([...kbKey, 'article', a.id], a);
      invalidate();
    },
  });
}

export function useDeleteKbArticle() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/kb/articles/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

// ── Website sources ─────────────────────────────────────
/** Polls while any import is running, so status and page counts update live. */
export function useKbSources(enabled = true) {
  return useQuery({
    queryKey: [...kbKey, 'sources'],
    enabled,
    queryFn: ({ signal }) => api<DataEnvelope<KbWebSource[]>>('/kb/sources', { signal }).then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'crawling') ? 2_500 : false),
  });
}

/** When an import finishes, its articles and category counts change too. */
export function useRefreshKbOnImport(sources: KbWebSource[] | undefined) {
  const qc = useQueryClient();
  const crawling = sources?.filter((s) => s.status === 'crawling').length ?? 0;
  const [prev, setPrev] = useState(crawling);
  if (crawling !== prev) {
    setPrev(crawling);
    if (crawling < prev) void qc.invalidateQueries({ queryKey: kbKey });
  }
}

export function useAddKbSource() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: (input: CreateKbWebSourceInput) =>
      api<DataEnvelope<KbWebSource>>('/kb/sources', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useSyncKbSource() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: (id: string) =>
      api<DataEnvelope<KbWebSource>>(`/kb/sources/${id}/sync`, { method: 'POST', body: {} }).then(
        (r) => r.data,
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteKbSource() {
  const invalidate = useInvalidateKb();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/kb/sources/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
